import { ref, computed, unref } from "vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import {
  API_KEYS,
  GENERATE_API_CREDENTIAL,
  REVOKE_API_KEY,
} from "../../graphql/sd_api";

// Resolves the sd_api Apollo client from a component's `$apollo` — shared by the
// card and the dialog instead of each resolving the path inline.
export function resolveSdApiClient(apollo) {
  return apollo.provider.clients.apolloSdApiClient;
}

// Credential generation is stateless (client + variables are passed in), so it stands
// on its own — the dialog uses just this rather than instantiating the full
// `useApiIntegration` state (keys/revoking) it doesn't need.
export function useGenerateCredential() {
  const { addAlert } = useAlerts();

  const generateCredential = async (apolloClient, variables) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: GENERATE_API_CREDENTIAL,
        variables,
      });
      return data.generateApiCredential;
    } catch (error) {
      addAlert({
        type: "error",
        message: "Error generating API credential",
        timeout: 10,
      });
      throw error;
    }
  };

  return { generateCredential };
}

export function useApiIntegration(facilityId) {
  const { addAlert } = useAlerts();
  const { generateCredential } = useGenerateCredential();

  const keys = ref([]);
  const revoking = ref(false);

  // Config a new credential inherits: the active key, else the most recent.
  const latestKey = computed(
    () => keys.value.find((k) => k.status === "active") || keys.value[0] || null
  );

  const fetchKeys = async (apolloClient) => {
    const organizationId = unref(facilityId);
    if (!organizationId || !apolloClient) return;

    try {
      const { data } = await apolloClient.query({
        query: API_KEYS,
        variables: { organizationId },
        fetchPolicy: "no-cache",
      });
      keys.value = data.apiKeys || [];
    } catch (error) {
      addAlert({
        type: "error",
        message: "Error fetching API credentials",
        timeout: 10,
      });
      keys.value = [];
    }
  };

  const revokeKey = async (apolloClient, apiKeyId = null) => {
    const organizationId = unref(facilityId);
    if (!organizationId || !apolloClient) return;

    revoking.value = true;
    try {
      await apolloClient.mutate({
        mutation: REVOKE_API_KEY,
        // Target the specific key the user clicked (defensive — safe today under the
        // one-active-key-per-org invariant, correct if it ever changes).
        variables: { organizationId, apiKeyId },
      });
      await fetchKeys(apolloClient);
    } catch (error) {
      addAlert({
        type: "error",
        message: "Error revoking API credential",
        timeout: 10,
      });
    } finally {
      revoking.value = false;
    }
  };

  return {
    keys,
    revoking,
    latestKey,
    fetchKeys,
    generateCredential,
    revokeKey,
  };
}
